import React, { useEffect, useRef, useCallback, useState } from 'react';

export const useInterval = (callback, delay, options = {}) => {
  const {
    immediate = false,
    enabled = true,
  } = options;

  const savedCallback = useRef(callback);
  const intervalRef = useRef(null);

  // Remember the latest callback
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval
  useEffect(() => {
    // Don't set up interval if disabled or delay is null
    if (!enabled || delay === null) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Call immediately if requested
    if (immediate) {
      savedCallback.current();
    }

    // Set up interval
    intervalRef.current = setInterval(() => {
      savedCallback.current();
    }, delay);

    // Cleanup
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [delay, enabled, immediate]);

  // Manual control functions
  const pause = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  const resume = useCallback(() => {
    if (!intervalRef.current && delay !== null && enabled) {
      intervalRef.current = setInterval(() => {
        savedCallback.current();
      }, delay);
    }
  }, [delay, enabled]);

  const reset = useCallback(() => {
    pause();
    if (enabled && delay !== null) {
      if (immediate) {
        savedCallback.current();
      }
      intervalRef.current = setInterval(() => {
        savedCallback.current();
      }, delay);
    }
  }, [delay, enabled, immediate, pause]);

  return {
    pause,
    resume,
    reset,
  };
};

/**
 * Hook for data fetching with automatic refresh interval
 * Common pattern for periodic data updates
 *
 * @param {Function} fetchFunction - Async function to fetch data
 * @param {number} intervalSeconds - Refresh interval in seconds (0 to disable)
 * @param {Object} options - Additional options
 * @returns {Object} Fetch control utilities
 */
export const useAutoRefresh = (fetchFunction, intervalSeconds = 60, options = {}) => {
  const {
    immediate = true,
    enabled = true,
    dependencies = [],
  } = options;

  const fetchRef = useRef(fetchFunction);
  const isInitialMount = useRef(true);

  // Update fetch function ref
  useEffect(() => {
    fetchRef.current = fetchFunction;
  }, [fetchFunction]);

  // Initial fetch
  useEffect(() => {
    if (enabled && immediate && isInitialMount.current) {
      fetchRef.current();
      isInitialMount.current = false;
    }
  }, [enabled, immediate]);

  // Set up interval for auto-refresh
  const intervalControls = useInterval(
    () => fetchRef.current(),
    intervalSeconds > 0 && enabled ? intervalSeconds * 1000 : null,
    { immediate: false, enabled }
  );

  // Refetch when dependencies change
  useEffect(() => {
    if (!isInitialMount.current && enabled) {
      fetchRef.current();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);

  const refetch = useCallback(() => {
    fetchRef.current();
  }, []);

  return {
    ...intervalControls,
    refetch,
  };
};

/**
 * Hook for managing refresh intervals with localStorage persistence
 * Commonly used in dashboard components
 *
 * @param {Function} fetchFunction - Async function to fetch data
 * @param {Object} options - Configuration options
 * @returns {Object} Interval management utilities
 */
export const useRefreshInterval = (fetchFunction, options = {}) => {
  const {
    defaultInterval = 60,
    storageKey = 'refreshInterval',
    immediate = true,
    enabled = true,
    dependencies = [],
  } = options;

  const intervalRef = useRef(null);
  const fetchRef = useRef(fetchFunction);

  // Get initial interval from localStorage
  const getStoredInterval = useCallback(() => {
    try {
      const stored = localStorage.getItem(storageKey);
      return stored ? parseInt(stored, 10) : defaultInterval;
    } catch (e) {
      return defaultInterval;
    }
  }, [storageKey, defaultInterval]);

  const [intervalSeconds, setIntervalSeconds] = useState(getStoredInterval);

  // Update fetch function ref
  useEffect(() => {
    fetchRef.current = fetchFunction;
  }, [fetchFunction]);

  // Initial fetch
  useEffect(() => {
    if (enabled && immediate) {
      fetchRef.current();
    }
  }, [enabled, immediate]);

  // Set up interval
  useEffect(() => {
    if (!enabled || intervalSeconds <= 0) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Clear existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Set up new interval
    intervalRef.current = setInterval(() => {
      fetchRef.current();
    }, intervalSeconds * 1000);

    // Cleanup
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [intervalSeconds, enabled]);

  // Refetch when dependencies change
  useEffect(() => {
    if (enabled && dependencies.length > 0) {
      fetchRef.current();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);

  // Listen for interval change events
  useEffect(() => {
    const handleIntervalChange = (event) => {
      const newInterval = event.detail;
      setIntervalSeconds(newInterval);
    };

    window.addEventListener('refreshIntervalChange', handleIntervalChange);

    return () => {
      window.removeEventListener('refreshIntervalChange', handleIntervalChange);
    };
  }, []);

  // Listen for refresh all events
  useEffect(() => {
    const handleRefreshAll = () => {
      fetchRef.current();
    };

    window.addEventListener('refreshAllData', handleRefreshAll);

    return () => {
      window.removeEventListener('refreshAllData', handleRefreshAll);
    };
  }, []);

  const refetch = useCallback(() => {
    fetchRef.current();
  }, []);

  return {
    intervalSeconds,
    setIntervalSeconds,
    refetch,
  };
};
